import styled from 'styled-components';

import { SPACING } from '~/common/constants/spacing';
import { RADIUS } from '~/common/constants/radius';
import { Skeleton } from '~/common/components/Skeleton';

export const PromptListContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACING._24};
  width: 100%;
  min-width: 100%;

  @media (max-width: 600px) {
    gap: ${SPACING._24};
  }
`;

export const HomeFooter = styled.footer`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: ${SPACING._8} 0 ${SPACING._24};
`;

export const HomeFooterContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${SPACING._12};
  flex-wrap: wrap;
  color: ${({ theme }) => theme.neutral500};
  font-size: 12px;
  line-height: 1.5;
  text-align: center;

  a {
    color: ${({ theme }) => theme.neutral500};
    text-decoration: none;
    transition: color 200ms ease;
  }

  a:hover {
    color: ${({ theme }) => theme.neutral300};
  }

  span[aria-hidden='true'] {
    opacity: 0.6;
  }

  @media (max-width: 600px) {
    gap: ${SPACING._8};
  }
`;

export const ProgressiveAccessNotice = styled.div`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.neutral800};
  background: ${({ theme }) => theme.neutral950};
  border-radius: ${RADIUS._8};
  padding: ${SPACING._16};
`;

export const PromptSection = styled.section`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${SPACING._16};
`;

export const PromptSectionHeader = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${SPACING._8};
  padding: ${SPACING._16};
  border: 1px solid ${({ theme }) => theme.neutral800};
  background: ${({ theme }) => theme.neutral950};
  border-radius: ${RADIUS._8};
`;

export const PromptFiltersWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${SPACING._24};

  @media (max-width: 800px) {
    flex-direction: column;
    gap: ${SPACING._16};
  }
`;

export const PromptCategoryFilterContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: center;
  gap: ${SPACING._16};
  width: 100%;

  @media (max-width: 750px) {
    flex-direction: column;
    > button {
      width: 100%;
      justify-content: center;
      height: 40px;
    }
  }
`;

export const SkeletonLoader = styled(Skeleton)`
  position: relative;
  height: 320px;
  border-radius: 10px;
  overflow: hidden;
  flex: 0 0 calc((100% - 60px) / 4); /* 4 colunas, 3 gaps de 20px = 60px */
  max-width: 300px;
  min-width: 264px;
  animation: fade-in 200ms ease-out;
  @keyframes fade-in {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @media (max-width: 1260px) {
    flex: 0 0 calc((100% - 40px) / 3); /* 3 colunas, 2 gaps */
    max-width: none;
  }

  @media (max-width: 1030px) {
    flex: 0 0 calc((100% - 20px) / 2); /* 2 colunas, 1 gap */
  }
`;

export const PromptList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  width: 100%;
  max-width: 100%;
  list-style: none;
  padding: 0;
  margin: 0;
  padding-bottom: ${SPACING._16};
  justify-content: center;
  animation: fade-in-up 400ms ease-out;
  @keyframes fade-in-up {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  a {
    width: 100%;
  }

  > span {
    height: 320px !important;
  }

  @media (max-width: 580px) {
    gap: 12px;

    > span {
      height: 260px !important;
    }
  }
`;

export const FiltersWrapper = styled.button`
  opacity: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${SPACING._6};
  border-radius: ${RADIUS._4};
  background-color: ${({ theme }) => theme.primary};
  cursor: pointer;
  transition: 200ms;
  position: relative;

  svg {
    transition: 200ms;
  }

  &:hover {
    filter: brightness(1.25);
  }
`;

export const FiltersCountElement = styled.div<{
  visible: boolean;
}>`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.red};
  padding: 2px;
  border-radius: 50%;
  transition: 200ms;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  pointer-events: none;
`;

export const CategoryBadgeWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

export const RefineIconButton = styled.button<{ $visible: boolean }>`
  position: absolute;
  top: -6px;
  right: -6px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.neutral700};
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  cursor: pointer;
  border: none;
  padding: 0;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  transition: opacity 200ms ease, filter 200ms ease;

  &:hover {
    filter: brightness(1.3);
  }
`;

export const RefineCountBadge = styled.span<{ $visible: boolean }>`
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 16px;
  height: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.red};
  color: ${({ theme }) => theme.neutral0};
  font-size: 10px;
  font-weight: 700;
  padding: 0 4px;
  border-radius: 50%;
  pointer-events: none;
  transition: 200ms;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
`;
